import React, { useEffect, useRef, useCallback } from 'react';
import { websocketService, WebSocketEvent } from '../../../shared/services/websocketService';
import { bannerService, TelaBanner, ProdutoBanner, resolveProdutoBannerCategoriaNome } from '../../banners/services/bannerService';
import {
  layoutStyleFromTelaConfiguracoes,
  mergePanfletoContent,
  mergePanfletoContentPreserving,
  parsePanfletoContentFromApiConfig,
  extrairPosicaoDoJson,
  extrairPaginacaoDoJson,
  extrairZoomDoJson,
  extrairNumeroColunasDoJson,
  extrairLarguraColunaDoJson,
  apiTelaToLocalScreen,
  TIPO_TO_LAYOUT,
  extrairDescricaoDoJson,
} from '../../banners/services/bannerSyncService';
import { Screen, Promotion, LayoutType, LayoutStyle } from '../../../shared/types';
import { televisaoService } from '../../televisao/services/televisaoService';

// Tipo compatível com TelevisaoDisplayPage
interface LoadedScreen {
  screen: Screen;
  promotions: Promotion[];
  videoBlobUrl?: string | null;
  position?: { posicaoX?: number; posicaoY?: number };
  productsPerPage?: number;
  tempoTelevisao?: number;
  tempoFinal?: number;
  zoomLevel?: number;
  numeroColunas?: number;
  larguraColuna?: number;
  configuracoes?: string;
}

interface UseTelevisaoWebSocketOptions {
  televisaoId: number | null;
  loadedScreens: LoadedScreen[];
  telasAPIDataRef: React.MutableRefObject<Map<number, TelaBanner>>;
  onScreensUpdated: (updatedScreens: LoadedScreen[]) => void;
  onTelevisaoConfigUpdated?: (dsTelasCadastradas: string | null, dsConfiguracoes: string | null) => void;
  promotions?: Promotion[]; // Promoções do store para preencher dados das telas
  enabled?: boolean;
}

/**
 * Hook para receber atualizações de televisão via WebSocket
 * Monitora todas as telas vinculadas à televisão
 */
export const useTelevisaoWebSocket = ({
  televisaoId,
  loadedScreens,
  telasAPIDataRef,
  onScreensUpdated,
  onTelevisaoConfigUpdated,
  promotions = [],
  enabled = true
}: UseTelevisaoWebSocketOptions) => {
  const isMountedRef = useRef<boolean>(true);
  // Refs para armazenar callbacks de atualização direta de telas
  const screenUpdateCallbacksRef = useRef<Map<number, () => void>>(new Map());
  // Ref para armazenar o estado atual das telas (para uso nos callbacks)
  const loadedScreensRef = useRef<LoadedScreen[]>(loadedScreens);
  // Ref para callback de atualização de televisão
  const televisaoUpdateCallbackRef = useRef<(() => void) | null>(null);
  // Refs para valores que mudam - evitar re-registrar callback
  const handleTelevisaoUpdatedRef = useRef<((data: any) => void) | null>(null);
  const handleScreenUpdatedRef = useRef<((screenId: number, updatedScreen: Screen, updatedTelaAPI: TelaBanner) => void) | null>(null);

  /**
   * Callback para quando uma tela específica é atualizada via SignalR
   * IMPORTANTE: Aplicar estilos DIRETAMENTE, SEM CACHE, igual ao DisplayPage
   */
  const handleScreenUpdated = useCallback((screenId: number, updatedScreen: Screen, updatedTelaAPI: TelaBanner) => {
    // Usar ref para obter o estado mais recente das telas
    const currentLoadedScreens = loadedScreensRef.current;
    
    if (!enabled || !televisaoId || currentLoadedScreens.length === 0 || !isMountedRef.current) {
      return;
    }

    try {
      // Encontrar a tela na lista de telas carregadas (usando ref atualizado)
      const screenIndex = currentLoadedScreens.findIndex(ls => ls.screen.id === screenId);
      if (screenIndex === -1) {
          return;
        }

      const loadedScreen = currentLoadedScreens[screenIndex];

      // Extrair configurações atualizadas (SEM CACHE, DIRETO DA API)
          const position = extrairPosicaoDoJson(updatedTelaAPI.configuracoes);
          const productsPerPage = extrairPaginacaoDoJson(updatedTelaAPI.configuracoes);
          const zoomLevel = extrairZoomDoJson(updatedTelaAPI.configuracoes);
          const numeroColunas = extrairNumeroColunasDoJson(updatedTelaAPI.configuracoes);
          const larguraColuna = extrairLarguraColunaDoJson(updatedTelaAPI.configuracoes);

      // Mesma regra do editor: reforço panfleto a partir do JSON (fallback tolerante abaixo)
      let layoutStyle: LayoutStyle | undefined = layoutStyleFromTelaConfiguracoes(updatedTelaAPI.configuracoes);
      if (!layoutStyle && updatedTelaAPI.configuracoes && updatedTelaAPI.configuracoes.trim() !== '' && updatedTelaAPI.configuracoes.trim() !== 'string') {
        try {
          const config = JSON.parse(updatedTelaAPI.configuracoes);
          const ls: any = {};
          if (config.productName) {
            ls.productName = {
              color: config.productName.color || '#000000',
              fontSize: config.productName.fontSize ?? 16,
              fontFamily: config.productName.fontFamily || 'Arial, sans-serif',
              lineHeight: config.productName.lineHeight ?? 1.2,
              letterSpacing: config.productName.letterSpacing ?? 0,
              fontWeight: config.productName.fontWeight ?? 400,
              verticalStretch: config.productName.verticalStretch ?? 100,
              showCurrency: config.productName.showCurrency !== undefined ? config.productName.showCurrency : true,
            };
          }
          if (config.price) {
            ls.price = {
              color: config.price.color || '#000000',
              fontSize: config.price.fontSize ?? 16,
              fontFamily: config.price.fontFamily || 'Arial, sans-serif',
              lineHeight: config.price.lineHeight ?? 1.2,
              letterSpacing: config.price.letterSpacing ?? 0,
              fontWeight: config.price.fontWeight ?? 400,
              verticalStretch: config.price.verticalStretch ?? 100,
              showCurrency: config.price.showCurrency !== undefined ? config.price.showCurrency : true,
            };
          }
          if (config.unit) {
            ls.unit = {
              color: config.unit.color || '#000000',
              fontSize: config.unit.fontSize ?? 16,
              fontFamily: config.unit.fontFamily || 'Arial, sans-serif',
              lineHeight: config.unit.lineHeight ?? 1.2,
              letterSpacing: config.unit.letterSpacing ?? 0,
              fontWeight: config.unit.fontWeight ?? 400,
              verticalStretch: config.unit.verticalStretch ?? 100,
              showCurrency: config.unit.showCurrency !== undefined ? config.unit.showCurrency : true,
            };
          }
          if (config.productContainer) {
            ls.productContainer = { ...config.productContainer };
          }
          if (config.description) ls.description = { ...config.description };
          if (config.category) ls.category = { ...config.category };
          if (config.validUntil) ls.validUntil = { ...config.validUntil };
          if (config.priceBox) {
            ls.priceBox = {
              enabled: config.priceBox.enabled ?? false,
              backgroundColor: config.priceBox.backgroundColor || '#ffffff',
              borderColor: config.priceBox.borderColor || '#000000',
              borderWidth: config.priceBox.borderWidth ?? 0,
              borderRadius: config.priceBox.borderRadius ?? 8,
              paddingTop: config.priceBox.paddingTop ?? 4,
              paddingRight: config.priceBox.paddingRight ?? 8,
              paddingBottom: config.priceBox.paddingBottom ?? 4,
              paddingLeft: config.priceBox.paddingLeft ?? 8,
              marginTop: config.priceBox.marginTop ?? 0,
              marginRight: config.priceBox.marginRight ?? 0,
              marginBottom: config.priceBox.marginBottom ?? 0,
              marginLeft: config.priceBox.marginLeft ?? 0,
            };
          }
          const panfletoRaw = parsePanfletoContentFromApiConfig(config);
          if (panfletoRaw) {
            ls.panfletoContent = mergePanfletoContentPreserving(
              loadedScreen.screen.layoutStyle?.panfletoContent,
              mergePanfletoContent(panfletoRaw)
            );
          }
          if (Object.keys(ls).length > 0) {
            layoutStyle = {
              ...(loadedScreen.screen.layoutStyle || {}),
              ...ls,
              priceBox: ls.priceBox || (loadedScreen.screen.layoutStyle as any)?.priceBox,
              panfletoContent:
                ls.panfletoContent || (loadedScreen.screen.layoutStyle as LayoutStyle | undefined)?.panfletoContent,
            } as LayoutStyle;
          }
        } catch (e) {
          console.warn(`[TelevisaoWebSocket] Erro ao parsear configuracoes:`, e);
        }
      }

      // Atualizar dados da API no ref
      telasAPIDataRef.current.set(screenId, updatedTelaAPI);

      // CRÍTICO: Atualizar também os produtos/promoções (igual fazemos no carregamento inicial)
      // Processar produtoIds da API para criar promoções atualizadas
      // IMPORTANTE: Se não houver produtos (0 produtos, null, undefined, ou array vazio), SEMPRE usar array vazio
      // Isso garante que quando zerar os produtos, a lista seja limpa e exiba "Nenhuma promoção encontrada"
      let screenPromotions: Promotion[] = [];
      
      // Verificar se produtoIds existe, é array e tem itens
      const produtoIds = updatedTelaAPI.produtoIds || null;
      
      // CRÍTICO: Se produtoIds existe e é um array válido com itens, processar
      // Caso contrário, screenPromotions permanece como [] (array vazio)
      if (produtoIds && Array.isArray(produtoIds) && produtoIds.length > 0) {
        screenPromotions = produtoIds.map((produto: any) => {
          const produtoId = produto.produtoId || produto;
          const categoriaDaApi =
            typeof produto === 'object' && produto !== null
              ? resolveProdutoBannerCategoriaNome(produto as ProdutoBanner & Record<string, unknown>)
              : '';

          const promotion: Promotion = {
            id: typeof produtoId === 'number' ? produtoId : (produto.produtoId || 0),
            name: produto.nome || 'Produto sem nome',
            price: produto.vlPreco || 0,
            originalPrice: produto.vlPrecoNormal || undefined,
            unit: produto.tipoUnidade || 'UN',
            image: undefined,
            video: undefined,
            description: undefined,
            category: categoriaDaApi || 'Geral',
            validUntil: produto.dtFinalCampanha
              ? new Date(produto.dtFinalCampanha).toISOString()
              : '',
            validFrom:
              produto.dtInicialCampanha || produto.dtInicioCampanha
                ? new Date(produto.dtInicialCampanha || produto.dtInicioCampanha).toISOString()
                : undefined,
            isVideoPromotion: false,
            hasAudio: false,
            autoplay: false,
            loop: false,
            createdAt: new Date().toISOString()
          };

          // Buscar promoção no store se existir (para pegar imagem, vídeo, etc)
          const localPromo = promotions.find(p => p.id === promotion.id);
          if (localPromo) {
            const storeCat = (localPromo.category || '').trim();
            const categoryFromStore =
              storeCat && storeCat !== 'Sem categoria' ? storeCat : '';
            return {
              ...promotion,
              name: localPromo.name || promotion.name,
              image: localPromo.image,
              video: localPromo.video,
              description: localPromo.description,
              category: categoriaDaApi || categoryFromStore || 'Geral',
              isVideoPromotion: localPromo.isVideoPromotion,
              hasAudio: localPromo.hasAudio,
              autoplay: localPromo.autoplay,
              loop: localPromo.loop
            };
          }

          return promotion;
        });
      }
      
      // CRÍTICO: Se produtoIds não existe, é null, undefined, não é array, ou é array vazio,
      // screenPromotions já está como [] (inicializado na linha 137)
      // Isso garante que quando zerar os produtos, a lista seja limpa e exiba "Nenhuma promoção encontrada"

      // Criar nova lista de telas com a tela atualizada (usando ref atualizado)
      // CRÍTICO: Sempre usar screenPromotions (pode ser array vazio se 0 produtos)
      // O componente TelevisaoDisplayPage já tem a mensagem "Nenhuma promoção encontrada" pronta
      // Quando promotions.length === 0, o componente exibe a mensagem automaticamente
      // IMPORTANTE: Screen.promotions é number[] (IDs), LoadedScreen.promotions é Promotion[]
      // CRÍTICO: Sempre extrair promotionIds de screenPromotions (não de updatedScreen.promotions)
      // Isso garante que quando zerar os produtos, os IDs também sejam limpos
      const promotionIds = screenPromotions.map(p => p.id);
      
      // CRÍTICO: Criar updatedScreen sem promotions para evitar sobrescrever com dados antigos
      // Extrair todas as propriedades de updatedScreen EXCETO promotions
      const { promotions: _, ...updatedScreenWithoutPromotions } = updatedScreen;
      
      const updatedScreens = [...currentLoadedScreens];
      updatedScreens[screenIndex] = {
            ...loadedScreen,
            screen: {
              ...loadedScreen.screen,
              ...updatedScreenWithoutPromotions, // Spread SEM promotions (evita sobrescrever com dados antigos)
              layoutStyle: layoutStyle || loadedScreen.screen.layoutStyle, // Usar novo layoutStyle ou manter o antigo
              promotions: promotionIds // CRÍTICO: Sempre usar promotionIds de screenPromotions (pode ser [] se 0 produtos)
            },
            promotions: screenPromotions, // CRÍTICO: Sempre usar screenPromotions (pode ser [] se 0 produtos)
            position,
            productsPerPage,
            zoomLevel,
            numeroColunas,
            larguraColuna,
            configuracoes: updatedTelaAPI.configuracoes,
      };

      // Atualizar ref com o novo estado
      loadedScreensRef.current = updatedScreens;

      // Atualizar estado imediatamente
      if (isMountedRef.current) {
        onScreensUpdated(updatedScreens);
      }
    } catch (error) {
      console.error(`[TelevisaoWebSocket] Erro ao atualizar tela ${screenId}:`, error);
    }
  }, [enabled, televisaoId, telasAPIDataRef, onScreensUpdated, promotions]);

  /**
   * Carrega telas novas em background (sem atualizar o estado até terminar tudo)
   * Retorna as telas carregadas para serem aplicadas de uma vez só
   */
  const loadNewScreensInBackground = useCallback(async (
    telaIds: number[],
    configsTelevisao: Map<number, any>,
    temposTelevisao: Map<number, number>,
    promotionsList: Promotion[]
  ): Promise<LoadedScreen[]> => {
    if (!isMountedRef.current || telaIds.length === 0) {
      return [];
    }

    try {
      const newScreens: LoadedScreen[] = [];

      // Carregar TODAS as telas primeiro (sem atualizar estado)
      for (const telaId of telaIds) {
        if (!isMountedRef.current) break;

        try {
          // Buscar tela da API
          const telasAPI = await bannerService.listarTelas(telaId);
          const telaAPI = telasAPI.find(t => t.id === telaId);

          if (!telaAPI || !telaAPI.id) {
            continue;
          }

          // Armazenar dados da API
          telasAPIDataRef.current.set(telaId, telaAPI);

          // Converter para Screen
          const layoutId = telaAPI.tipo !== undefined
            ? (TIPO_TO_LAYOUT[telaAPI.tipo] || 'simple2col') as LayoutType
            : 'simple2col' as LayoutType;

          let description = telaAPI.descricao || '';
          if (!description && telaAPI.configuracoes) {
            const descricaoDoJson = extrairDescricaoDoJson(telaAPI.configuracoes);
            if (descricaoDoJson) {
              description = descricaoDoJson;
            }
          }

          const layoutStyle = layoutStyleFromTelaConfiguracoes(telaAPI.configuracoes);
          const position = extrairPosicaoDoJson(telaAPI.configuracoes);
          const paginacao = extrairPaginacaoDoJson(telaAPI.configuracoes);
          const productsPerPage = paginacao && paginacao > 0 ? paginacao : 20;

          const tempoTelevisao = temposTelevisao.get(telaId);
          const tempoFinal = tempoTelevisao !== undefined && tempoTelevisao !== null && !isNaN(tempoTelevisao) && tempoTelevisao > 0
            ? Number(tempoTelevisao)
            : undefined;

          const zoomLevel = extrairZoomDoJson(telaAPI.configuracoes) ?? 1;
          const numeroColunas = extrairNumeroColunasDoJson(telaAPI.configuracoes);
          const larguraColuna = extrairLarguraColunaDoJson(telaAPI.configuracoes);

          const fullScreen: Screen = {
            id: telaAPI.id,
            name: telaAPI.nome,
            description: description,
            layoutId: layoutId,
            promotions: telaAPI.produtoIds ? telaAPI.produtoIds.map((p: any) => p.produtoId || p) : [],
            isActive: telaAPI.ativo ?? false,
            backgroundColor: '#ffffff',
            backgroundImage: undefined,
            showHeader: true,
            duration: 30,
            layoutStyle: layoutStyle,
            createdAt: new Date().toISOString()
          };

          // Buscar promoções da API (seguindo padrão do TelevisaoDisplayPage)
          let screenPromotions: Promotion[] = [];
          if (telaAPI.produtoIds && Array.isArray(telaAPI.produtoIds) && telaAPI.produtoIds.length > 0) {
            screenPromotions = telaAPI.produtoIds.map((produto: any) => {
              const produtoId = produto.produtoId || produto;
              const categoriaDaApi =
                typeof produto === 'object' && produto !== null
                  ? resolveProdutoBannerCategoriaNome(produto as ProdutoBanner & Record<string, unknown>)
                  : '';

              const promotion: Promotion = {
                id: typeof produtoId === 'number' ? produtoId : (produto.produtoId || 0),
                name: produto.nome || 'Produto sem nome',
                price: produto.vlPreco || 0,
                originalPrice: produto.vlPrecoNormal || undefined,
                unit: produto.tipoUnidade || 'UN',
                image: undefined,
                video: undefined,
                description: undefined,
                category: categoriaDaApi || 'Geral',
                validUntil: produto.dtFinalCampanha
                  ? new Date(produto.dtFinalCampanha).toISOString()
                  : '',
                validFrom:
                  produto.dtInicialCampanha || produto.dtInicioCampanha
                    ? new Date(produto.dtInicialCampanha || produto.dtInicioCampanha).toISOString()
                    : undefined,
                isVideoPromotion: false,
                hasAudio: false,
                autoplay: false,
                loop: false,
                createdAt: new Date().toISOString()
              };

              // Buscar promoção no store se existir
              const localPromo = promotionsList.find(p => p.id === promotion.id);
              if (localPromo) {
                const storeCat = (localPromo.category || '').trim();
                const categoryFromStore =
                  storeCat && storeCat !== 'Sem categoria' ? storeCat : '';
                return {
                  ...promotion,
                  name: localPromo.name || promotion.name,
                  image: localPromo.image,
                  video: localPromo.video,
                  description: localPromo.description,
                  category: categoriaDaApi || categoryFromStore || 'Geral',
                  isVideoPromotion: localPromo.isVideoPromotion,
                  hasAudio: localPromo.hasAudio,
                  autoplay: localPromo.autoplay,
                  loop: localPromo.loop
                };
              }

              return promotion;
            });
          }

          // Pré-carregar vídeo se necessário
          let videoBlobUrl: string | null = null;
          if (layoutId === 'video' && telaAPI.id) {
            try {
              const videoBlob = await bannerService.downloadVideo(telaAPI.id);
              if (videoBlob && videoBlob.size > 0) {
                videoBlobUrl = URL.createObjectURL(videoBlob);
              }
            } catch (videoError) {
              console.error(`Erro ao pré-carregar vídeo da tela ${telaAPI.id}:`, videoError);
            }
          }

          newScreens.push({
            screen: fullScreen,
            promotions: screenPromotions,
            videoBlobUrl: videoBlobUrl,
            position: position,
            productsPerPage: productsPerPage,
            tempoTelevisao: tempoTelevisao,
            tempoFinal: tempoFinal,
            zoomLevel: zoomLevel,
            numeroColunas: numeroColunas,
            larguraColuna: larguraColuna,
            configuracoes: telaAPI.configuracoes,
          });
        } catch (error) {
          console.error(`[TelevisaoWebSocket] Erro ao carregar tela ${telaId}:`, error);
        }
      }

      return newScreens;
    } catch (error) {
      console.error(`[TelevisaoWebSocket] Erro ao carregar telas:`, error);
      return [];
    }
  }, [telasAPIDataRef, promotions]);

  /**
   * Handler para quando a televisão é atualizada via SignalR
   * IMPORTANTE: O websocketService faz GET da televisão e passa os dados completos aqui
   * CRÍTICO: Aplicar alterações DIRETAMENTE, SEM REINICIAR A TELA (padrão DisplayPage)
   */
  const handleTelevisaoUpdated = useCallback(async (televisaoData: any) => {
    if (!enabled || !televisaoId || !isMountedRef.current) {
      return;
    }

    // IMPORTANTE: Permitir processar atualização mesmo quando loadedScreens.length === 0
    // Isso permite detectar quando telas são adicionadas a uma televisão que estava sem telas

    try {
      // Extrair dados da televisão recebidos do GET (objeto Televisao completo)
      const dsTelasCadastradas = televisaoData?.dsTelasCadastradas ?? televisaoData?.ds_telas_cadastradas ?? null;
      const dsConfiguracoes = televisaoData?.dsConfiguracoes ?? televisaoData?.ds_configuracoes ?? null;
      const nmTelevisao = televisaoData?.nmTelevisao ?? televisaoData?.nm_televisao ?? null;

      // Notificar componente pai sobre mudança de configuração (se callback fornecido)
      if (onTelevisaoConfigUpdated) {
        onTelevisaoConfigUpdated(dsTelasCadastradas, dsConfiguracoes);
      }

      // Se não houver mudanças nas telas cadastradas e configurações, não fazer nada
      // (os estilos das telas individuais são tratados pelos callbacks de telas)
      if (!dsTelasCadastradas && !dsConfiguracoes) {
        return;
      }

      // LÓGICA: Seguir EXATAMENTE a configuração, sem assumir nada
      // 1. Ver as telas cadastradas (dsTelasCadastradas)
      // 2. Dentro delas, ver a ordem que elas estão (na configuração)
      // 3. Por último, ver se é dia delas serem exibidas (dias na configuração)

      // PASSO 1: Extrair TODAS as configurações das telas (tempos, ordens, dias)
      const configsTelevisao = televisaoService.extrairTodasConfigs(dsConfiguracoes);
      const temposTelevisao = televisaoService.extrairTodosTempos(dsConfiguracoes);

      // PASSO 2: Extrair TODOS os IDs das telas do ds_telas_cadastradas
      // Estas são as telas que estão cadastradas na televisão
      const todasTelaIds = televisaoService.stringToTelaIds(dsTelasCadastradas);
      

      // PASSO 3: Obter dia da semana atual (0=Domingo, 1=Segunda, ..., 6=Sábado)
      const hoje = new Date();
      const diaSemanaAtual = hoje.getDay().toString();

      // PASSO 4: Para cada tela cadastrada, verificar:
      // - Se tem configuração
      // - Qual a ordem dela (PASSO 2)
      // - Se o dia atual está nos dias configurados (PASSO 3)
      const telasDoDia: Array<{ id: number; ordem: number; config: any }> = [];
      
      for (const telaId of todasTelaIds) {
        const config = configsTelevisao.get(telaId);
        
        if (!config) {
          // Se não tem configuração, não incluir (não está configurada)
          continue;
        }
        
        // PASSO 2: Ver a ordem dela
        // CRÍTICO: SEMPRE ler a ordem das configurações - SEMPRE vai ter uma ordem
        // A ordem pode ter gaps (ex: 1, 2, 3, 4, 5, 6) mas apenas algumas telas estarão no dia
        // Então se tela 2 tem ordem 2 e tela 5 tem ordem 5, devem aparecer nessa ordem: 2, 5
        const ordem = typeof config.ordem === 'number' ? config.ordem : (typeof config.ordem === 'string' ? parseInt(config.ordem, 10) : 0);
        
        // PASSO 3: Ver se é dia delas serem exibidas
        const diasTela = config.dias || [];
        
        if (diasTela.length === 0) {
          // Se não tem dias configurados, não incluir
          continue;
        }
        
        if (!diasTela.includes(diaSemanaAtual)) {
          // Dia atual não está na lista de dias da tela
          continue;
        }
        
        // Tela passou em todos os critérios - adicionar com sua ordem
        telasDoDia.push({
          id: telaId,
          ordem: ordem,
          config: config
        });
      }

      // PASSO 5: Ordenar telas pela ordem configurada
      // CRÍTICO: SEMPRE ordenar pela ordem que vem nas configurações
      // SEMPRE vai ter uma ordem - seguir ela SEMPRE
      telasDoDia.sort((a, b) => {
        // Ordenar por ordem crescente (ordem menor vem primeiro)
        // SEMPRE usar a ordem das configurações
        return a.ordem - b.ordem;
      });

      // PASSO 6: Extrair apenas os IDs na ordem correta (seguindo EXATAMENTE a configuração)
      const telaIdsOrdenadas = telasDoDia.map(t => t.id);
      
      // CRÍTICO: Se não houver telas para o dia atual, limpar a lista (0 telas)
      // O componente TelevisaoDisplayPage já tem a mensagem bonita pronta com ícone, fundo, etc
      // A mensagem aparece quando: !initialLoading && loadedScreens.length === 0 && loadingProgress.total === 0
      // Apenas limpar a lista - o componente vai exibir a mensagem automaticamente quando as condições forem atendidas
      if (telaIdsOrdenadas.length === 0) {
        loadedScreensRef.current = [];
        if (isMountedRef.current) {
          // Limpar a lista - o componente TelevisaoDisplayPage vai detectar e mostrar a mensagem
          onScreensUpdated([]);
        }
        return;
      }

      // CRÍTICO: NÃO ASSUMIR NADA - seguir EXATAMENTE o que a configuração diz
      // A configuração define: quais telas, em que ordem, e se é dia delas
      
      // Criar mapa das telas atualmente carregadas (para reutilizar se ainda existirem)
      const currentLoadedScreens = loadedScreensRef.current;
      const screensMap = new Map<number, LoadedScreen>();
      currentLoadedScreens.forEach(ls => {
        screensMap.set(ls.screen.id, ls);
      });

      // CRÍTICO: Carregar TODAS as telas primeiro, só aplicar quando estiver tudo pronto
      // Evitar atualizações parciais que causam "piscar" de telas
      
      // Separar telas que já estão carregadas das que precisam ser carregadas
      const telasExistentes: Array<{ telaId: number; screen: LoadedScreen }> = [];
      const telasParaCarregar: number[] = [];

      // Seguir EXATAMENTE a ordem definida na configuração
      for (const telaId of telaIdsOrdenadas) {
        const existingScreen = screensMap.get(telaId);
        
        if (existingScreen) {
          // Tela já estava carregada - reutilizar
          telasExistentes.push({ telaId, screen: existingScreen });
        } else {
          // Tela não estava carregada - marcar para carregar
          telasParaCarregar.push(telaId);
        }
      }
      

      // Se houver telas para carregar, carregar em background (sem travar a UI)
      // A tela atual continua sendo exibida normalmente
      if (telasParaCarregar.length > 0) {
        // Carregar todas as telas novas em background (assíncrono, não bloqueia)
        // A tela atual continua sendo exibida normalmente
        loadNewScreensInBackground(telasParaCarregar, configsTelevisao, temposTelevisao, promotions)
          .then((telasCarregadas) => {
            if (!isMountedRef.current) return;
            
            // Atualizar o mapa com as telas recém-carregadas
            telasCarregadas.forEach(loadedScreen => {
              screensMap.set(loadedScreen.screen.id, loadedScreen);
            });
            
            // AGORA sim, construir a lista final com TODAS as telas (existentes + recém-carregadas)
            // Seguindo EXATAMENTE a ordem da configuração
            const updatedScreens: LoadedScreen[] = [];
            
            for (const telaId of telaIdsOrdenadas) {
              const screen = screensMap.get(telaId);
              if (!screen) {
                continue;
              }
              
              // Atualizar tempo conforme configuração
              const tempoTelevisao = temposTelevisao.get(telaId);
              const tempoFinal = tempoTelevisao !== undefined && tempoTelevisao !== null && !isNaN(tempoTelevisao) && tempoTelevisao > 0
                ? Number(tempoTelevisao)
                : undefined;
              
              updatedScreens.push({
                ...screen,
                tempoTelevisao,
                tempoFinal
              });
            }
            
            // Atualizar ref com nova ordem e configurações
            loadedScreensRef.current = updatedScreens;

            // APLICAR alterações APENAS quando tudo estiver pronto (de forma fluida)
            if (isMountedRef.current) {
              // Atualizar estado diretamente (igual DisplayPage faz) - de forma fluida
              onScreensUpdated(updatedScreens);
            }
          })
          .catch((error) => {
            console.error(`[TelevisaoWebSocket] Erro ao carregar telas em background:`, error);
          });
        
        // Se não houver telas para carregar, aplicar imediatamente (apenas reordenar/atualizar tempos)
      } else {
        // Todas as telas já estão carregadas - apenas reordenar e atualizar tempos
        const updatedScreens: LoadedScreen[] = [];
        
        for (const telaId of telaIdsOrdenadas) {
          const screen = screensMap.get(telaId);
          if (!screen) {
            continue;
          }
          
          // Atualizar tempo conforme configuração
          const tempoTelevisao = temposTelevisao.get(telaId);
          const tempoFinal = tempoTelevisao !== undefined && tempoTelevisao !== null && !isNaN(tempoTelevisao) && tempoTelevisao > 0
            ? Number(tempoTelevisao)
            : undefined;
          
          updatedScreens.push({
            ...screen,
            tempoTelevisao,
            tempoFinal
          });
        }
        
        // Atualizar ref com nova ordem e configurações
        loadedScreensRef.current = updatedScreens;

        // APLICAR alterações de forma fluida (sem recarregar página)
        if (isMountedRef.current) {
          // Atualizar estado diretamente (igual DisplayPage faz) - de forma fluida
          onScreensUpdated(updatedScreens);
        }
      }
    } catch (error) {
      console.error(`[TelevisaoWebSocket] Erro ao processar atualização da televisão ${televisaoId}:`, error);
    }
  }, [enabled, televisaoId, onScreensUpdated, onTelevisaoConfigUpdated]);


  // Atualizar refs quando valores mudarem
  useEffect(() => {
    handleTelevisaoUpdatedRef.current = handleTelevisaoUpdated;
    handleScreenUpdatedRef.current = handleScreenUpdated;
  }, [handleTelevisaoUpdated, handleScreenUpdated]);

  // Atualizar ref quando loadedScreens mudar
  useEffect(() => {
    loadedScreensRef.current = loadedScreens;
  }, [loadedScreens]);

  // Configurar monitoramento - MESMO PADRÃO DO useScreenWebSocket
  // CRÍTICO: Registrar callback IMEDIATAMENTE quando tiver televisaoId
  // Não esperar por enabled/loadedScreens - o callback processa tudo
  useEffect(() => {
    isMountedRef.current = true;

    // Registrar callback IMEDIATAMENTE quando tiver televisaoId
    if (!televisaoId) {
      return;
    }

    // Conectar e registrar callback DIRETAMENTE aqui, igual useScreenWebSocket
    const setup = async () => {
    try {
      // Conectar ao WebSocket se não estiver conectado
      if (!websocketService.isConnected()) {
        await websocketService.connect({
          autoReconnect: true,
          reconnectInterval: 3000,
          maxReconnectAttempts: 10
        });
      }

        // Remover callback anterior se existir
        if (televisaoUpdateCallbackRef.current) {
          televisaoUpdateCallbackRef.current();
          televisaoUpdateCallbackRef.current = null;
        }
        
        // Registrar callback DIRETO para televisão (igual useScreenWebSocket faz para tela)
        const unsubscribeTelevisao = websocketService.onTelevisaoUpdate(televisaoId, (data: any) => {
          if (handleTelevisaoUpdatedRef.current) {
            handleTelevisaoUpdatedRef.current(data);
          }
        });
        televisaoUpdateCallbackRef.current = unsubscribeTelevisao;

      // Subscribe para atualizações da televisão
      await websocketService.subscribeTelevisao(televisaoId);
      } catch (error) {
        console.error(`[TelevisaoWebSocket] ❌ Erro ao configurar WebSocket:`, error);
      }
    };

    setup();

    return () => {
      isMountedRef.current = false;
      if (televisaoUpdateCallbackRef.current) {
        televisaoUpdateCallbackRef.current();
        televisaoUpdateCallbackRef.current = null;
      }
      if (televisaoId) {
        websocketService.offTelevisaoUpdate(televisaoId);
        websocketService.unsubscribeTelevisao(televisaoId).catch(console.error);
      }
    };
  }, [televisaoId]); // Sem dependências - usa refs, igual useScreenWebSocket

  // Registrar callbacks de telas quando loadedScreens mudar
  // IMPORTANTE: Funciona mesmo quando loadedScreens.length === 0 (para detectar quando telas são adicionadas)
  useEffect(() => {
    if (!enabled || !televisaoId || !websocketService.isConnected()) {
      return;
    }

    // Se não houver telas, não precisa registrar callbacks de telas (mas o hook continua ativo para televisão)
    if (loadedScreens.length === 0) {
      return;
    }

    // Registrar callback DIRETO para cada tela (igual useScreenWebSocket)
    const setupScreens = async () => {
      for (const loadedScreen of loadedScreens) {
        const screenId = loadedScreen.screen.id;
        
        // Se não tiver callback registrado, registrar agora
        if (!screenUpdateCallbacksRef.current.has(screenId)) {
          await websocketService.subscribeScreen(screenId);
          const unsubscribeCallback = websocketService.onTelevisaoScreenUpdate(screenId, (updatedScreen: Screen, updatedTelaAPI: TelaBanner) => {
            if (handleScreenUpdatedRef.current) {
              handleScreenUpdatedRef.current(screenId, updatedScreen, updatedTelaAPI);
            }
          });
          screenUpdateCallbacksRef.current.set(screenId, unsubscribeCallback);
        }
      }
    };

    setupScreens();

    return () => {
      // Cleanup será feito no cleanup geral
    };
  }, [enabled, televisaoId, loadedScreens]); // Sem handleScreenUpdated - usa ref

  // Cleanup de callbacks de telas quando componente desmontar ou telas mudarem
  useEffect(() => {
    return () => {
      // Remover todos os callbacks de telas ao desmontar
      const callbacksArray = Array.from(screenUpdateCallbacksRef.current.entries());
      for (const [screenId, unsubscribeCallback] of callbacksArray) {
        unsubscribeCallback();
        websocketService.unsubscribeScreen(screenId).catch(console.error);
        websocketService.offTelevisaoScreenUpdate(screenId);
      }
      screenUpdateCallbacksRef.current.clear();
    };
  }, []);

};
